/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: field_change_suggestions entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the field_change_suggestions table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the field_change_suggestions API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, target_table, target_record_id, field_name, current_value_snapshot, suggested_value, note, suggested_by_user_profile_id, suggested_at, status, reviewed_by_user_profile_id, reviewed_at, decision_note, is_junction, updated_at]
 * Searchable fields: []
 * Filterable fields: [id, target_record_id, suggested_by_user_profile_id, status, reviewed_by_user_profile_id]
 */

import { httpClient } from '../http-client.js';
import { type UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  FieldChangeSuggestionsRow,
  FieldChangeSuggestionsInsert,
  FieldChangeSuggestionsUpdate
} from '../../types/generated_schemas/generated-field_change_suggestions-schema.js';

export interface FieldChangeSuggestionsQueryOptions extends UnifiedQueryOptions {}

export interface FieldChangeSuggestionsResponse {
  data: FieldChangeSuggestionsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for field_change_suggestions table operations
 *
 * @description Provides type-safe access to field_change_suggestions API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class FieldChangeSuggestionsClient {
  private readonly basePath = '/field-change-suggestions';

  /**
   * Get all field_change_suggestions records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of field_change_suggestions records
   */
  async getAll(options?: FieldChangeSuggestionsQueryOptions): Promise<FieldChangeSuggestionsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sort', options.sortBy);
    if (options?.sortOrder) params.set('order', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<FieldChangeSuggestionsResponse>(url);
    return response.data;
  }

  /**
   * Get a single field_change_suggestions record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the field_change_suggestions record
   */
  async getById(id: string): Promise<FieldChangeSuggestionsRow> {
    const response = await httpClient.get<{
      data: FieldChangeSuggestionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new field_change_suggestions record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created field_change_suggestions record
   */
  async create(data: FieldChangeSuggestionsInsert): Promise<FieldChangeSuggestionsRow> {
    const response = await httpClient.post<{
      data: FieldChangeSuggestionsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing field_change_suggestions record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated field_change_suggestions record
   */
  async update(id: string, data: FieldChangeSuggestionsUpdate): Promise<FieldChangeSuggestionsRow> {
    const response = await httpClient.put<{
      data: FieldChangeSuggestionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a field_change_suggestions record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple field_change_suggestions records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created field_change_suggestions records
   */
  async createMany(data: FieldChangeSuggestionsInsert[]): Promise<FieldChangeSuggestionsRow[]> {
    const response = await httpClient.post<{
      data: FieldChangeSuggestionsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Search field_change_suggestions with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: FieldChangeSuggestionsQueryOptions
  ): Promise<FieldChangeSuggestionsResponse> {
    throw new Error('Search operations are not available for field_change_suggestions - no searchable fields configured');
  }

  /**
   * Get count of field_change_suggestions records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a field_change_suggestions record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'field_change_suggestions',
      publicFields: ['id', 'target_table', 'target_record_id', 'field_name', 'current_value_snapshot', 'suggested_value', 'note', 'suggested_by_user_profile_id', 'suggested_at', 'status', 'reviewed_by_user_profile_id', 'reviewed_at', 'decision_note', 'is_junction', 'updated_at'],
      searchableFields: [],
      filterableFields: ['id', 'target_record_id', 'suggested_by_user_profile_id', 'status', 'reviewed_by_user_profile_id'],
      sortableFields: ['updated_at'],
      readonlyFields: ['id', 'updated_at'],
      requiredFields: ['id', 'target_table', 'target_record_id', 'field_name', 'suggested_value', 'suggested_by_user_profile_id', 'suggested_at', 'status', 'is_junction', 'updated_at'],
    };
  }
}

// Export singleton instance for convenience
export const fieldChangeSuggestionsClient = new FieldChangeSuggestionsClient();
